import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';
import errorLogger from '../utils/errorHandler';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log el error
    errorLogger.log(error, {
      type: 'REACT_ERROR_BOUNDARY',
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown'
    });

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Si hay demasiados errores, recarga la página
    if (this.state.errorCount > 3) {
      console.error('Demasiados errores, recargando página...');
      setTimeout(() => window.location.reload(), 2000);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Si hay una función de reset personalizada, ejecutarla
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI personalizado
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
              ¡Ups! Algo salió mal
            </h1>
            
            <p className="text-gray-600 text-center mb-6">
              Lo sentimos, ha ocurrido un error inesperado. Nuestro equipo ha sido notificado
              y estamos trabajando para solucionarlo.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 overflow-auto max-h-60">
                <p className="text-sm font-mono text-red-600 mb-2">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={this.handleReset}
                className="bg-blue-900 hover:bg-blue-800 text-white"
                size="lg"
              >
                <RefreshCw className="w-5 h-5 mr-2" />
                Intentar de Nuevo
              </Button>
              
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                size="lg"
              >
                <Home className="w-5 h-5 mr-2" />
                Ir al Inicio
              </Button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500 mb-2">
                ¿Necesitas ayuda inmediata?
              </p>
              <a
                href="tel:+529515020840"
                className="text-blue-900 hover:text-blue-700 font-semibold"
              >
                +52 951 502 0840
              </a>
              <span className="text-gray-400 mx-2">|</span>
              <a
                href="mailto:ventasaerotucan@gmail.com"
                className="text-blue-900 hover:text-blue-700 font-semibold"
              >
                ventasaerotucan@gmail.com
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
